/** @format */

import {
	Box,
	Button,
	Chip,
	Container,
	Grid,
	Pagination,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyFilter2 } from "../../utility/utils/utils";
import CircleContributionCard from "./CircleContributionCard";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	selectAllCirclesOfMember,
	selectCurrentUser,
} from "../../redux/selectors/userSelector";
import {
	AccountVerificationModal,
	LoadingSpinner,
	// ReactivateSubscription,
	// SubscriptionModal,
} from "../allComponents/AllComponents";
import { buttonColors, colors } from "../../utility/types/constant";
import { AppDispatch } from "../../redux/store/store";
import {
	fetchJoinRequestUserSent,
	getAccountVerificationStatus,
} from "../../redux/actions/circleAction";
import { getMissedContributions } from "../../redux/actions/autoDraftAction";
import { fetchAllCirclesOfMember } from "../../redux/actions/userActions";
import { selectJointRequestUserSent } from "../../redux/selectors/circleSelector";

// Animation keyframes
const fadeInScale = keyframes`
	0% {
		opacity: 0;
		transform: scale(0.9) translateY(20px);
	}
	100% {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
`;

const iconFloat = keyframes`
	0%, 100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-8px);
	}
`;

// Divides evenly into the 2- and 3-column card layouts
const CIRCLES_PER_PAGE = 6;

const ContributionTable = () => {
	const navigate = useNavigate();

	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const userCircles = useSelector(selectAllCirclesOfMember);

	const [page, setPage] = useState(1);

	const circlesSectionRef = useRef<HTMLElement | null>(null);

	const theme = useTheme();

	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [openVerifyAccountModal, setOpenVerifyAccountModal] = useState(false);

	const [isNavigating, setIsNavigating] = useState(false);

	useEffect(() => {
		if (userData?._id) {
			dispatch(getAccountVerificationStatus(userData._id));
		}
	}, [dispatch, userData?._id]);

	// Simple callback to refresh table data after successful contribution
	const handleTableRefresh = useCallback(() => {
		if (userData?._id) {
			dispatch(fetchAllCirclesOfMember(userData._id));
			dispatch(getMissedContributions());
		}
	}, [dispatch, userData?._id]);

	useEffect(() => {
		if (userData?.isVerified) {
			dispatch(fetchJoinRequestUserSent(userData?._id ?? ""));
		}
	}, [dispatch, userData?._id, userData?.isVerified]);

	const joinRequestUserSend = useSelector(selectJointRequestUserSent);

	const handleOpenVerifyAccountModal = useCallback(() => {
		setOpenVerifyAccountModal(true);
	}, []);

	const handleCloseVerifyAccountModal = () => {
		setOpenVerifyAccountModal(false);
	};

	// Get list of circle IDs where the user has a pending join request
	const pendingJoinRequestCircleIds = useMemo(() => {
		if (!joinRequestUserSend || !Array.isArray(joinRequestUserSend)) {
			return new Set<string>();
		}
		return new Set(
			joinRequestUserSend
				.filter((request: any) => request?.status === "Pending")
				.map((request: any) => {
					// Handle both populated circleId object and plain string
					const circleId = request?.circleId?._id || request?.circleId;
					return typeof circleId === "string" ? circleId : circleId?.toString();
				})
				.filter(Boolean),
		);
	}, [joinRequestUserSend]);

	// Filter out circles where the user has a pending join request
	const circlesWithConfirmedMembership = useMemo(() => {
		const circles = userCircles?.circles || [];
		if (pendingJoinRequestCircleIds.size === 0) {
			return circles;
		}
		return circles.filter((circle: any) => {
			const circleId = circle?._id || circle;
			return !pendingJoinRequestCircleIds.has(circleId);
		});
	}, [userCircles?.circles, pendingJoinRequestCircleIds]);

	const dataFiltered = applyFilter2({
		inputData: circlesWithConfirmedMembership,
	});

	const handleNavigateToCircle = (circleId: string) => {
		setIsNavigating(true);
		navigate(`/circle/${circleId}`);
	};

	// clear spinner when component umnount
	useEffect(() => {
		return () => {
			setIsNavigating(false);
		};
	}, []);

	const pageCount = Math.max(
		1,
		Math.ceil(dataFiltered.length / CIRCLES_PER_PAGE),
	);
	// Clamp so leaving a circle on the last page never shows an empty page
	const currentPage = Math.min(page, pageCount);
	const pageStart = (currentPage - 1) * CIRCLES_PER_PAGE;
	const visibleCircles = dataFiltered.slice(
		pageStart,
		pageStart + CIRCLES_PER_PAGE,
	);

	const handlePageChange = (_event: unknown, value: number) => {
		setPage(value);
		circlesSectionRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	};

	const isAdminOrSuperUser = userData?.roles?.some(
		(role: any) => role.roleName === "Admin" || role.roleName === "Super Admin",
	);

	const handleRedirectToJoinCircles = () => {
		return navigate(`/circles`, { replace: true });
	};

	return (
		<Container>
			{/* 
      {
        userData?.isVerified && !userData?.stripeCustomerId && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          // mt={5}
        >
          <Paper
            sx={{
              p: 3,
              maxWidth: "600px",
              margin: " 0 auto",
              mb: 3,
              textAlign: "center",
              backgroundColor: "#fff7e6",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              {verificationMessage}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              Your subscription is currently
                  {userSubscription?.subscriptionStatus}
                  <strong>{userSubscription?.subscriptionStatus}</strong>.
              You are currently not a member. Please activate to gain full
              access.
            </Typography>

            {
              getSubscriptionMessage() && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "1rem",
                    fontWeight: 700,
                    marginBottom: 2,
                  }}
                >
                  {getSubscriptionMessage()}
                </Typography>
              </>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleOpenSubscriptionModal}
              sx={{
                backgroundColor: buttonColors.blueRibbon,
                fontSize: "1em",
                "&:hover": {
                  backgroundColor: buttonColors.blueRibbon,
                },
              }}
            >
              Activate Membership
            </Button>
            <SubscriptionModal
              open={openSubscriptionModal}
              handleClose={handleCloseSubscriptionModal}
            />
          </Paper>
        </Stack>
      )} */}

			{userData && !userData.isVerified && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						px: 2,
						my: 3,
					}}
				>
					<Paper
						elevation={0}
						sx={{
							p: { xs: 4, sm: 5 },
							maxWidth: "500px",
							width: "100%",
							margin: "0 auto",
							borderRadius: "24px",
							background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
							boxShadow:
								"0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)",
							border: "1px solid rgba(255, 255, 255, 0.8)",
							animation: `${fadeInScale} 0.6s ease-out`,
							position: "relative",
							overflow: "hidden",
							"&::before": {
								content: '""',
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								height: "4px",
								background: `linear-gradient(90deg, ${buttonColors.blueRibbon}, #00d4ff)`,
							},
							"&:hover": {
								transform: "translateY(-4px)",
								boxShadow:
									"0 25px 70px rgba(0, 0, 0, 0.12), 0 10px 30px rgba(0, 0, 0, 0.1)",
							},
							transition: "all 0.3s ease",
						}}
					>
						<Stack
							direction="column"
							alignItems="center"
							justifyContent="center"
							spacing={3}
						>
							{/* Animated Icon */}
							<Box
								sx={{
									width: 80,
									height: 80,
									borderRadius: "50%",
									background: `linear-gradient(135deg, ${buttonColors.blueRibbon}20, ${buttonColors.blueRibbon}10)`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									animation: `${iconFloat} 3s ease-in-out infinite`,
								}}
							>
								<VerifiedUserOutlinedIcon
									sx={{
										fontSize: 40,
										color: buttonColors.blueRibbon,
									}}
								/>
							</Box>

							{/* Title */}
							<Typography
								variant="h5"
								sx={{
									fontFamily: "'Montserrat', sans-serif",
									fontSize: { xs: "1.25rem", sm: "1.5rem" },
									fontWeight: 700,
									textAlign: "center",
									color: "#1a1a2e",
									lineHeight: 1.3,
								}}
							>
								Verify Your Identity
							</Typography>

							{/* Description */}
							<Typography
								variant="body1"
								sx={{
									fontFamily: "'Montserrat', sans-serif",
									fontSize: { xs: "0.9rem", sm: "1rem" },
									fontWeight: 400,
									textAlign: "center",
									color: "#64748b",
									lineHeight: 1.6,
									maxWidth: "380px",
								}}
							>
								To gain full access to our platform and start participating in
								circles, please complete the verification process.
							</Typography>

							{/* CTA Button */}
							<Button
								variant="contained"
								size="large"
								onClick={handleOpenVerifyAccountModal}
								sx={{
									px: 5,
									py: 1.5,
									background: `linear-gradient(135deg, ${buttonColors.blueRibbon} 0%, #0052cc 100%)`,
									fontSize: "1rem",
									fontWeight: 600,
									fontFamily: "'Montserrat', sans-serif",
									borderRadius: "12px",
									textTransform: "none",
									boxShadow: `0 4px 15px ${buttonColors.blueRibbon}40`,
									"&:hover": {
										background: `linear-gradient(135deg, #0052cc 0%, ${buttonColors.blueRibbon} 100%)`,
										boxShadow: `0 6px 20px ${buttonColors.blueRibbon}50`,
										transform: "translateY(-2px)",
									},
									transition: "all 0.3s ease",
								}}
							>
								Verify Identity
							</Button>

							{/* Subtle footer text */}
							<Typography
								variant="caption"
								sx={{
									fontFamily: "'Montserrat', sans-serif",
									color: "#94a3b8",
									fontSize: "0.75rem",
									textAlign: "center",
								}}
							>
								Secure verification powered by Stripe
							</Typography>
						</Stack>
					</Paper>
				</Box>
			)}

			{dataFiltered.length === 0 &&
				joinRequestUserSend &&
				Array.isArray(joinRequestUserSend) &&
				joinRequestUserSend.length > 0 && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							px: 2,
							my: 3,
						}}
					>
						<Paper
							elevation={0}
							sx={{
								p: { xs: 4, sm: 5 },
								maxWidth: "500px",
								width: "100%",
								margin: "0 auto",
								borderRadius: "24px",
								background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
								boxShadow:
									"0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)",
								border: "1px solid rgba(255, 255, 255, 0.8)",
								animation: `${fadeInScale} 0.6s ease-out`,
								position: "relative",
								overflow: "hidden",
								"&::before": {
									content: '""',
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									height: "4px",
									background: `linear-gradient(90deg, ${buttonColors.blueRibbon}, #00d4ff)`,
								},
								"&:hover": {
									transform: "translateY(-4px)",
									boxShadow:
										"0 25px 70px rgba(0, 0, 0, 0.12), 0 10px 30px rgba(0, 0, 0, 0.1)",
								},
								transition: "all 0.3s ease",
							}}
						>
							<Stack
								direction="column"
								alignItems="center"
								justifyContent="center"
								spacing={3}
							>
								{/* Animated Icon */}
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: "50%",
										background: `linear-gradient(135deg, ${buttonColors.blueRibbon}20, ${buttonColors.blueRibbon}10)`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										animation: `${iconFloat} 3s ease-in-out infinite`,
									}}
								>
									<HourglassTopOutlinedIcon
										sx={{
											fontSize: 40,
											color: buttonColors.blueRibbon,
										}}
									/>
								</Box>

								{/* Title */}
								<Typography
									variant="h5"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontSize: { xs: "1.25rem", sm: "1.5rem" },
										fontWeight: 700,
										textAlign: "center",
										color: "#1a1a2e",
										lineHeight: 1.3,
									}}
								>
									Request Under Review
								</Typography>

								{/* Description */}
								<Typography
									variant="body1"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontSize: { xs: "0.9rem", sm: "1rem" },
										fontWeight: 400,
										textAlign: "center",
										color: "#64748b",
										lineHeight: 1.6,
										maxWidth: "380px",
									}}
								>
									Your circle join request is currently under review. Please
									check back later for an update.
								</Typography>

								{/* Subtle footer text */}
								<Typography
									variant="caption"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										color: "#94a3b8",
										fontSize: "0.75rem",
										textAlign: "center",
									}}
								>
									Circle leaders typically respond within 24 hours
								</Typography>
							</Stack>
						</Paper>
					</Box>
				)}

			{dataFiltered.length === 0 &&
				userData?.isVerified === true &&
				Array.isArray(joinRequestUserSend) &&
				joinRequestUserSend.length === 0 &&
				!isAdminOrSuperUser && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							px: 2,
							my: 3,
						}}
					>
						<Paper
							elevation={0}
							sx={{
								p: { xs: 4, sm: 5 },
								maxWidth: "500px",
								width: "100%",
								margin: "0 auto",
								borderRadius: "24px",
								background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
								boxShadow:
									"0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)",
								border: "1px solid rgba(255, 255, 255, 0.8)",
								animation: `${fadeInScale} 0.6s ease-out`,
								position: "relative",
								overflow: "hidden",
								"&::before": {
									content: '""',
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									height: "4px",
									background: `linear-gradient(90deg, ${buttonColors.blueRibbon}, #00d4ff)`,
								},
								"&:hover": {
									transform: "translateY(-4px)",
									boxShadow:
										"0 25px 70px rgba(0, 0, 0, 0.12), 0 10px 30px rgba(0, 0, 0, 0.1)",
								},
								transition: "all 0.3s ease",
							}}
						>
							<Stack
								direction="column"
								alignItems="center"
								justifyContent="center"
								spacing={3}
							>
								{/* Animated Icon */}
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: "50%",
										background: `linear-gradient(135deg, ${buttonColors.blueRibbon}20, ${buttonColors.blueRibbon}10)`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										animation: `${iconFloat} 3s ease-in-out infinite`,
									}}
								>
									<GroupsOutlinedIcon
										sx={{
											fontSize: 40,
											color: buttonColors.blueRibbon,
										}}
									/>
								</Box>

								{/* Title */}
								<Typography
									variant="h5"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontSize: { xs: "1.25rem", sm: "1.5rem" },
										fontWeight: 700,
										textAlign: "center",
										color: "#1a1a2e",
										lineHeight: 1.3,
									}}
								>
									No Circles Yet
								</Typography>

								{/* Description */}
								<Typography
									variant="body1"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontSize: { xs: "0.9rem", sm: "1rem" },
										fontWeight: 400,
										textAlign: "center",
										color: "#64748b",
										lineHeight: 1.6,
										maxWidth: "380px",
									}}
								>
									You are currently not a member of any circle. Join or send a
									request to join a circle to get started.
								</Typography>

								{/* CTA Button */}
								<Button
									variant="contained"
									size="large"
									onClick={handleRedirectToJoinCircles}
									sx={{
										px: 5,
										py: 1.5,
										background: `linear-gradient(135deg, ${buttonColors.blueRibbon} 0%, #0052cc 100%)`,
										fontSize: "1rem",
										fontWeight: 600,
										fontFamily: "'Montserrat', sans-serif",
										borderRadius: "12px",
										textTransform: "none",
										boxShadow: `0 4px 15px ${buttonColors.blueRibbon}40`,
										"&:hover": {
											background: `linear-gradient(135deg, #0052cc 0%, ${buttonColors.blueRibbon} 100%)`,
											boxShadow: `0 6px 20px ${buttonColors.blueRibbon}50`,
											transform: "translateY(-2px)",
										},
										transition: "all 0.3s ease",
									}}
								>
									View & Join Circle
								</Button>

								{/* Subtle footer text */}
								<Typography
									variant="caption"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										color: "#94a3b8",
										fontSize: "0.75rem",
										textAlign: "center",
									}}
								>
									Browse available circles and start saving together
								</Typography>
							</Stack>
						</Paper>
					</Box>
				)}

			{/* <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        // mt={1}
        // mb={7}
      >
        {getSubscriptionMessage() && (
          <Paper
            sx={{
              p: 3,
              maxWidth: "800px",
              width: "100%",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "1rem",
                fontWeight: 700,
                marginBottom: 2,
              }}
            >
              {getSubscriptionMessage()}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleOpenReactivateModal}
              sx={{
                backgroundColor: buttonColors.blueRibbon,
                fontSize: "1em",
                "&:hover": {
                  backgroundColor: buttonColors.blueRibbon,
                },
              }}
            >
              Reactivate Subscription
            </Button>
            <ReactivateSubscription
              open={openReactivateSubscriptionModal}
              handleClose={handleCloseReactivateModal}
            />
          </Paper>
        )}
      </Stack> */}

			{/* {isError && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          // mt={7}
          // mb={5}
        >
          <Paper>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "1rem",
                fontWeight: 999,
              }}
            >
              Something unexpected happened. Please try later.
            </Typography>
          </Paper>
        </Stack>
      )} */}

			<AccountVerificationModal
				open={openVerifyAccountModal}
				handleClose={handleCloseVerifyAccountModal}
			/>

			{(
				(dataFiltered.length > 0 && userData?.isVerified === true) ||
				isAdminOrSuperUser
			) ?
				<Box
					component="section"
					ref={circlesSectionRef}
					aria-labelledby="my-circles-heading"
					sx={{ scrollMarginTop: 80 }}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 2 }}
					>
						<Box>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Typography
									id="my-circles-heading"
									component="h2"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontWeight: 700,
										fontSize: { xs: "1.15rem", sm: "1.3rem" },
										color: "#0f172a",
									}}
								>
									My Circles
								</Typography>
								<Chip
									label={dataFiltered.length}
									size="small"
									sx={{
										fontWeight: 700,
										bgcolor: "#eef2ff",
										color: colors.blueRibbon,
									}}
								/>
							</Stack>
							<Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
								Make your contributions and keep track of each circle
							</Typography>
						</Box>
					</Stack>

					{dataFiltered.length === 0 ?
						<Typography variant="body2" sx={{ color: "#64748b", py: 3 }}>
							You are not a member of any circle yet.
						</Typography>
					:	<Grid container spacing={2}>
							{visibleCircles.map((circle: any) => (
								<Grid item xs={12} sm={6} lg={4} key={circle._id}>
									<CircleContributionCard
										circle={circle}
										currentUserId={userData?._id ?? ""}
										onViewCircle={handleNavigateToCircle}
										onContributionSuccess={handleTableRefresh}
									/>
								</Grid>
							))}
						</Grid>
					}

					{pageCount > 1 && (
						<Stack alignItems="center" spacing={1} sx={{ mt: 3 }}>
							<Pagination
								count={pageCount}
								page={currentPage}
								onChange={handlePageChange}
								shape="rounded"
								size={isSmallScreen ? "medium" : "large"}
								siblingCount={isSmallScreen ? 0 : 1}
								aria-label="My circles pages"
								sx={{
									"& .MuiPaginationItem-root": {
										fontWeight: 600,
										minWidth: 40,
										height: 40,
									},
									"& .MuiPaginationItem-root.Mui-selected": {
										bgcolor: colors.blueRibbon,
										color: colors.white,
										"&:hover": { bgcolor: "#0f2d80" },
									},
								}}
							/>
							<Typography variant="caption" sx={{ color: "#64748b" }}>
								Showing {pageStart + 1}–{pageStart + visibleCircles.length} of{" "}
								{dataFiltered.length} circles
							</Typography>
						</Stack>
					)}
				</Box>
			:	null}

			{isNavigating && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(255,255,255,0.7)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
					}}
				>
					<LoadingSpinner
						loading={isNavigating}
						size={35}
						sx={{ mt: 5 }}
						color={colors.blue}
					/>
				</Box>
			)}
		</Container>
	);
};

export default ContributionTable;
